import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Input, Textarea, Select, Radio, Toggle } from 'components/FormFields';
import FormModal, { FormGroup } from 'components/FormModal';
import Toaster from 'components/Toaster';

const VideoFormModal = ({ video, topics, onSave, onClose }) => {
  const [formData, setFormData] = useState(
    video
      ? {
          mode: video.mode,
          title: video.title,
          description: video.description,
          vimeo_id: video.vimeo_id,
          duration_label: video.duration_label,
          topic_id: video.topic_id,
          is_new: video.is_new || false,
          is_featured: video.is_featured || false,
          status: video.status || 'draft',
          help_link: video.help_link || '',
        }
      : {
          mode: 'discover',
          title: '',
          description: '',
          vimeo_id: '',
          duration_label: '',
          topic_id: '',
          is_new: false,
          is_featured: false,
          status: 'draft',
          help_link: '',
        }
  );

  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({ isOpen: false });

  const filteredTopics = topics.filter((t) => t.mode === formData.mode);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (!formData.title.trim()) next.title = 'Title is required';
    if (!formData.description.trim())
      next.description = 'Description is required';
    if (!formData.vimeo_id.trim()) next.vimeo_id = 'Vimeo Video ID is required';
    if (!formData.duration_label.trim())
      next.duration_label = 'Duration is required';
    if (!formData.topic_id) next.topic_id = 'Topic is required';
    return next;
  };

  const handleSubmit = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Validation Error',
        message: Object.values(next)[0],
      });
      return;
    }

    onSave({
      ...formData,
      topic_id: parseInt(formData.topic_id) || undefined,
    });
  };

  return (
    <>
      <FormModal
        isOpen={true}
        onClose={onClose}
        title={video ? 'Edit Video' : 'Add New Video'}
        subtitle="Configure video details and settings"
        submitText={video ? 'Save Changes' : 'Add Video'}
        onSubmit={handleSubmit}
        isSubmitting={false}
      >
        <FormGroup>
          <Radio
            name="mode"
            label="Mode *"
            value={formData.mode}
            onChange={(e) => {
              setFormData({ ...formData, mode: e.target.value, topic_id: '' });
              setErrors((prev) =>
                prev.topic_id ? { ...prev, topic_id: undefined } : prev
              );
            }}
            inline
            options={[
              { label: 'Ideas & Tips (Marketing)', value: 'discover' },
              { label: 'How To Do It (Instructional)', value: 'howto' },
            ]}
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="Video Title"
            required
            id="title"
            name="title"
            value={formData.title}
            onChange={(e) => setField('title', e.target.value)}
            placeholder="e.g., Introducing Bundled Tickets"
            error={!!errors.title}
            helperText={errors.title}
          />
        </FormGroup>

        <FormGroup>
          <Textarea
            label="Description"
            required
            id="description"
            name="description"
            value={formData.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="Brief description..."
            rows={3}
            error={!!errors.description}
            helperText={errors.description}
          />
        </FormGroup>

        <FormGroup>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
            }}
          >
            <Input
              label="Vimeo Video ID"
              required
              id="vimeo_id"
              name="vimeo_id"
              value={formData.vimeo_id}
              onChange={(e) => setField('vimeo_id', e.target.value)}
              placeholder="e.g., 1118728475"
              error={!!errors.vimeo_id}
              helperText={
                errors.vimeo_id || (
                  <>
                    From vimeo.com/
                    <span style={{ color: '#FA4616' }}>1118728475</span>
                  </>
                )
              }
            />

            <Input
              label="Duration"
              required
              id="duration_label"
              name="duration_label"
              value={formData.duration_label}
              onChange={(e) => setField('duration_label', e.target.value)}
              placeholder="e.g., 3:45"
              error={!!errors.duration_label}
              helperText={errors.duration_label}
            />
          </div>
        </FormGroup>

        <FormGroup>
          <Select
            label="Topic"
            required
            id="topic_id"
            name="topic_id"
            value={formData.topic_id}
            onChange={(e) => setField('topic_id', e.target.value)}
            placeholder="Select a topic..."
            options={filteredTopics.map((topic) => ({
              label: topic.name,
              value: topic.id,
            }))}
            error={!!errors.topic_id}
            helperText={errors.topic_id}
          />
        </FormGroup>

        {formData.mode === 'howto' && (
          <FormGroup>
            <Input
              label="Help Documentation Link"
              id="help_link"
              name="help_link"
              type="url"
              value={formData.help_link || ''}
              onChange={(e) =>
                setFormData({ ...formData, help_link: e.target.value })
              }
              placeholder="https://promoters.pitpay.com/?faqs=..."
              helperText='Shown as "Learn More" link'
            />
          </FormGroup>
        )}

        <FormGroup>
          <div style={{ display: 'flex', gap: '24px', paddingTop: '8px' }}>
            <Toggle
              checked={formData.is_new}
              onChange={(e) =>
                setFormData({ ...formData, is_new: e.target.checked })
              }
              label="Mark as NEW"
            />
            {formData.mode === 'discover' && (
              <Toggle
                checked={formData.is_featured}
                onChange={(e) =>
                  setFormData({ ...formData, is_featured: e.target.checked })
                }
                label="Featured Video"
              />
            )}
          </div>
        </FormGroup>

        <FormGroup>
          <Select
            label="Status"
            id="status"
            name="status"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            options={[
              { label: 'Draft', value: 'draft' },
              { label: 'Published', value: 'published' },
            ]}
          />
        </FormGroup>

        {formData.vimeo_id && (
          <FormGroup>
            <div
              style={{
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Preview
            </div>
            <div
              style={{
                position: 'relative',
                paddingTop: '56.25%',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#000',
              }}
            >
              <iframe
                src={`https://player.vimeo.com/video/${formData.vimeo_id}?title=0&byline=0&portrait=0`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                frameBorder="0"
                allow="fullscreen"
                allowFullScreen
                title="Video preview"
              />
            </div>
          </FormGroup>
        )}
      </FormModal>
      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
      />
    </>
  );
};

VideoFormModal.propTypes = {
  video: PropTypes.object,
  topics: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VideoFormModal;
